import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';


import {USER_TABS} from 'src/constants';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {AuthenticationContext} from 'src/modules/authentication/authentication-context';
import {ActiveNotificationsViewer, activeNotificationsSelector} from 'src/modules/internal-notification';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {getUserActiveNotifications} from 'src/openapi/admin/apis/UserInternalNotificationApi';
import {useAppDispatch} from 'src/store/hooks';
import {getUserLabel} from 'src/utils/getUserLabel';

import {AvatarWrapped} from '@pb/kit-component-avatar';
import {FlatButton} from '@pb/kit-component-button';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';

import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Props = {
    userSortInfo: UserShortInfo,
};

const UserCornerView = ({userSortInfo}: Props) => {
    const navigate = useNavigate();
    const {requestLogout} = useContext(AuthenticationContext);
    const {logout: logoutTrack, userProfileOpen} = useEventTracking();

    const notificationsResponse = useSelector(activeNotificationsSelector);

    const logout = useCallback(() => {
        logoutTrack();
        requestLogout(true);
    }, [requestLogout, logoutTrack]);

    const redirectToUserProfile = useCallback(() => {
        userProfileOpen({
            place: 'User Corner',
        });
        navigate(`/profile/user/${userSortInfo.userId}/${USER_TABS.DETAILS.name}`);
    }, [userSortInfo.userId, userProfileOpen, navigate]);

    const redirectToInternalNotifications = useCallback(() => {
        navigate(`/internal-notification/my-notifications`);
    }, [navigate]);

    const caption = useMemo(() => (
        userSortInfo ? getUserLabel(userSortInfo) : ''
    ), [userSortInfo]);

    const whatsNewCaption = useMemo(() => (
        `What's new ${notificationsResponse?.data?.length ? `(${notificationsResponse.data.length})` : ''}`
    ), [notificationsResponse]);

    return (
        <>
            <DropdownMenu
                anchor={({positionRef, getAnchorProps, anchorRef}) => (

                    <div ref={anchorRef} {...getAnchorProps()} style={{cursor: 'pointer'}}>
                        <Group>
                            <AvatarWrapped
                                src={userSortInfo.avatar}
                                alt={userSortInfo.userId.toString()}
                                name={caption}
                                isCurrentUser={true}
                                placeholder={Icons.PERSON_ROUNDED}
                            />
                            <FlatButton endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}>
                                <b>{caption}</b>
                            </FlatButton>
                        </Group>
                    </div>
                )}>
                <DropdownMenuItem
                    iconBefore={Icons.USER_CIRCLE}
                    caption="My Profile"
                    onClick={redirectToUserProfile}
                />
                <DropdownMenuItem
                    iconBefore={Icons.BULLHORN}
                    caption={whatsNewCaption}
                    onClick={redirectToInternalNotifications}
                />
                <DropdownMenuItem
                    iconBefore={Icons.LOG_OUT}
                    separated={true}
                    caption="Logout"
                    onClick={logout}
                />
            </DropdownMenu>
            {notificationsResponse?.data ? (
                <ActiveNotificationsViewer userId={userSortInfo.userId} notifications={notificationsResponse.data}/>
            ) : null}
        </>
    );
};

export const UserCorner = () => {
    const apiDispatch = useAppDispatch();
    const userSortInfo = useSelector(selectUserShortInfo);

    useEffect(() => {
        apiDispatch(getUserActiveNotifications());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!userSortInfo) {
        return null;
    }

    return (
        <UserCornerView userSortInfo={userSortInfo}/>
    );
};
